// apps/web/src/pages/compras/pedido-compra-dialogs.tsx — Fase 13, Plano 19 (COMP-04, D-19; UI-SPEC § Cancelar pedido e Encerrar saldo).
// Confirmações destrutivas do pedido de compra: "Cancelar pedido" e "Encerrar saldo" do item. O botão vermelho (#dc2626) é
// reservado a elas; motivo obrigatório (botão desabilitado sem motivo); "Voltar" sempre dispensa; durante a requisição o
// botão tem spinner e o diálogo permanece aberto em erro (mensagem inline + toast da página). Escrita por apiMutate (CSRF).
import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import type { PedidoCompraItemView, PedidoCompraView } from '@pieltec/contracts';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { FormMessage } from '../../components/ui/form-message';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { apiMutate } from '../../lib/csrf-fetch';
import { lerErroApi } from '../../lib/vendas/erro-api';
import { formatarQuantidade } from '../../lib/vendas/formatacao';

/** Mesmo limite do DTO do servidor (`MotivoPedidoCompraDto`). */
export const MOTIVO_MAXIMO_CARACTERES = 500;

const MENSAGEM_FALHA = 'Não foi possível concluir a operação. Verifique a conexão e tente novamente.';

/** O motivo só vale com texto (o servidor recusa vazio com 400 MOTIVO_OBRIGATORIO). */
export function motivoPreenchido(texto: string): boolean {
  return texto.trim().length > 0;
}

/** Corpo do diálogo "Cancelar pedido" (UI-SPEC). */
export function corpoCancelarPedido(numero: string): string {
  return `Cancelar o pedido ${numero} encerra todos os itens ainda sem recebimento. Itens já recebidos não são alterados. O motivo fica registrado na auditoria. Confirmar cancelamento?`;
}

/** Corpo do diálogo "Encerrar saldo" (UI-SPEC); `numeroItem` é o número do item no pedido. */
export function corpoEncerrarSaldo(saldo: number, unidadeCompra: string, numeroItem: number): string {
  return `O saldo restante de ${formatarQuantidade(saldo)} ${unidadeCompra} do item ${numeroItem} será encerrado sem recebimento (ex.: o fornecedor não entregará o restante). O motivo fica registrado. Confirmar?`;
}

interface ConfirmacaoComMotivoProps {
  readonly aberto: boolean;
  readonly onAbertoChange: (aberto: boolean) => void;
  readonly titulo: string;
  readonly corpo: string;
  readonly rotuloMotivo: string;
  readonly rotuloConfirmar: string;
  readonly idCampo: string;
  /** POST do comando com o motivo; devolve a resposta HTTP. */
  readonly enviar: (motivo: string) => Promise<Response>;
  readonly onSucesso: (pedido: PedidoCompraView) => void;
  /** Falha: a página mostra o toast (o diálogo continua aberto). */
  readonly onFalha: (mensagem: string) => void;
}

function ConfirmacaoComMotivo({ aberto, onAbertoChange, titulo, corpo, rotuloMotivo, rotuloConfirmar, idCampo, enviar, onSucesso, onFalha }: ConfirmacaoComMotivoProps) {
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (!aberto) return;
    setMotivo('');
    setErro(null);
  }, [aberto]);

  const podeConfirmar = motivoPreenchido(motivo);

  async function confirmar(): Promise<void> {
    if (!podeConfirmar || enviando) return;
    setErro(null);
    setEnviando(true);
    try {
      const res = await enviar(motivo.trim());
      if (!res.ok) {
        const falha = await lerErroApi(res, MENSAGEM_FALHA);
        setErro(falha.mensagem);
        onFalha(falha.mensagem);
        return;
      }
      const pedido = (await res.json()) as PedidoCompraView;
      onSucesso(pedido);
      onAbertoChange(false);
    } catch {
      setErro(MENSAGEM_FALHA);
      onFalha(MENSAGEM_FALHA);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Dialog open={aberto} onOpenChange={(proximo) => (enviando ? undefined : onAbertoChange(proximo))}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{titulo}</DialogTitle>
          <DialogDescription>{corpo}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={idCampo}>{rotuloMotivo}</Label>
            <Textarea
              id={idCampo}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              maxLength={MOTIVO_MAXIMO_CARACTERES}
              disabled={enviando}
              autoFocus
            />
          </div>
          {erro && <FormMessage>{erro}</FormMessage>}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onAbertoChange(false)} disabled={enviando}>
            Voltar
          </Button>
          <Button type="button" variant="destructive" onClick={() => void confirmar()} disabled={!podeConfirmar || enviando}>
            {enviando && <Loader2 className="animate-spin" aria-hidden="true" />}
            {rotuloConfirmar}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface CancelarPedidoDialogProps {
  readonly aberto: boolean;
  readonly onAbertoChange: (aberto: boolean) => void;
  readonly pedido: Pick<PedidoCompraView, 'id' | 'numero'>;
  readonly onSucesso: (pedido: PedidoCompraView) => void;
  readonly onFalha: (mensagem: string) => void;
}

export function CancelarPedidoDialog({ aberto, onAbertoChange, pedido, onSucesso, onFalha }: CancelarPedidoDialogProps) {
  return (
    <ConfirmacaoComMotivo
      aberto={aberto}
      onAbertoChange={onAbertoChange}
      titulo="Cancelar pedido"
      corpo={corpoCancelarPedido(pedido.numero)}
      rotuloMotivo="Motivo (obrigatório)"
      rotuloConfirmar="Cancelar pedido"
      idCampo="cancelar-pedido-motivo"
      enviar={(motivo) => apiMutate(`/api/v1/compras/pedidos/${encodeURIComponent(pedido.id)}/cancelar`, { body: { motivo } })}
      onSucesso={onSucesso}
      onFalha={onFalha}
    />
  );
}

interface EncerrarSaldoDialogProps {
  readonly aberto: boolean;
  readonly onAbertoChange: (aberto: boolean) => void;
  readonly pedidoId: string;
  readonly item: Pick<PedidoCompraItemView, 'id' | 'numeroItem' | 'saldoAReceber' | 'unidadeCompra'>;
  readonly onSucesso: (pedido: PedidoCompraView) => void;
  readonly onFalha: (mensagem: string) => void;
}

export function EncerrarSaldoDialog({ aberto, onAbertoChange, pedidoId, item, onSucesso, onFalha }: EncerrarSaldoDialogProps) {
  return (
    <ConfirmacaoComMotivo
      aberto={aberto}
      onAbertoChange={onAbertoChange}
      titulo="Encerrar saldo"
      corpo={corpoEncerrarSaldo(item.saldoAReceber, item.unidadeCompra, item.numeroItem)}
      rotuloMotivo="Motivo (obrigatório)"
      rotuloConfirmar="Encerrar saldo"
      idCampo="encerrar-saldo-motivo"
      enviar={(motivo) =>
        apiMutate(`/api/v1/compras/pedidos/${encodeURIComponent(pedidoId)}/itens/${encodeURIComponent(item.id)}/encerrar-saldo`, { body: { motivo } })
      }
      onSucesso={onSucesso}
      onFalha={onFalha}
    />
  );
}
